<script setup lang="ts">
import { useNuxtApp } from '#app'
import IconLeaveFeedback from '@/components/ui/button/IconLeaveFeedback.vue'
// import IconRanaOfflineLight from '~/components/icons/IconRanaOfflineLight.vue';

import { RanaSignin } from '@monorepo/ui';

const { $vm: vm } = useNuxtApp()

const emit = defineEmits<{
  (e: 'open-login-panel' | 'leaveFeedback'): void
}>()

const signinRef = ref()
const IsThreeDotMenuOpen = ref(false)
function OpenThreeDotMenu() {
  IsThreeDotMenuOpen.value = !IsThreeDotMenuOpen.value
}
// Function to extract username from email wihtout exposing the full email
function getUsernameFromEmail(email: string): string {
  if (!email) return '';
  const atIndex = email.indexOf('@');
  return atIndex > 0 ? email.substring(0, atIndex) : email;
}

function openSigninDrawer() {
  signinRef.value?.open()
}

async function handleClickFeedback() {
  const module = await import('@monorepo/ui/components/Feedback.vue')
  // module.default is the component
  vm.openRightPanel(module.default)
}

function logout() {
  localStorage.removeItem('auth_token')
  vm.user.jwtToken = ''
  vm.user.loginExpires = ''
  vm.user.email = ''
  vm.user.countryCode = ''
}
</script>

<template>
  <div class="flex h-20 items-center justify-between rounded-lg bg-[hsl(var(--neutral))] px-4 max-w-72 w-72">
    <!-- Left side - User profile and info -->
    <div 
    class="flex h-16 items-center" :class="{
      'max-w-[60%]': $vm?.windowWidth < 1024,
      'max-w-[70%]': $vm?.windowWidth >= 768
    }">
      <!-- Profile icon -->
      <div class="relative rounded-full border-2  p-[2px] hover:bg-base-100/40 mr-2" :class="vm.user?.jwToken ? 'border-online' : 'border-[#00ccaa]'">
        <div class="relative flex h-10 w-10 items-center justify-center rounded-full">
          <div class="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-secondary">
            <!-- If logged in, show avatar; if not, show default user icon -->
            <template v-if="vm.user?.jwtToken">
              <svg 
              width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg"
                class="aspect-square h-[60%]">
                <path
                  d="M8 25.9524C8 24.0182 12.6764 21.1487 15.6977 20.962C15.8947 20.9498 16.0872 21.0109 16.262 21.1027C16.7938 21.3819 18.1116 21.9524 20 21.9524C21.8884 21.9524 23.2062 21.3819 23.738 21.1027C23.9128 21.0109 24.1053 20.9498 24.3023 20.962C27.3236 21.1487 32 24.0182 32 25.9524C32 27.9524 32 26.9524 32 28.9524C32 30.9524 30 32.9524 30 32.9524C30 32.9524 28 34.9524 20 34.9524C12 34.9524 10 32.9524 10 32.9524C10 32.9524 8 30.9524 8 28.9524C8 26.9524 8 27.9524 8 25.9524Z"
                  fill="#00CAAA" />
                <path
                  d="M26 13.9524C26 17.2661 23.3137 19.9524 20 19.9524C16.6863 19.9524 14 17.2661 14 13.9524C14 10.6387 16.6863 7.95239 20 7.95239C23.3137 7.95239 26 10.6387 26 13.9524Z"
                  fill="#00CAAA" />
              </svg>
            </template>
            <template v-else>
              <svg 
              width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg"
                class="aspect-square h-[60%]">
                <path
                  d="M8 25.9524C8 24.0182 12.6764 21.1487 15.6977 20.962C15.8947 20.9498 16.0872 21.0109 16.262 21.1027C16.7938 21.3819 18.1116 21.9524 20 21.9524C21.8884 21.9524 23.2062 21.3819 23.738 21.1027C23.9128 21.0109 24.1053 20.9498 24.3023 20.962C27.3236 21.1487 32 24.0182 32 25.9524C32 27.9524 32 26.9524 32 28.9524C32 30.9524 30 32.9524 30 32.9524C30 32.9524 28 34.9524 20 34.9524C12 34.9524 10 32.9524 10 32.9524C10 32.9524 8 30.9524 8 28.9524C8 26.9524 8 27.9524 8 25.9524Z"
                  fill="#00ccaa" />
                <path
                  d="M26 13.9524C26 17.2661 23.3137 19.9524 20 19.9524C16.6863 19.9524 14 17.2661 14 13.9524C14 10.6387 16.6863 7.95239 20 7.95239C23.3137 7.95239 26 10.6387 26 13.9524Z"
                  fill="#00ccaa" />
              </svg>
            </template>
          </div>
        </div>
      </div>

      <div class="flex flex-col overflow-hidden text-sm md:text-base ">
        <span 
        class=" font-bold text-accent whitespace-nowrap max-w-24 truncate" :class="{
          'text-md': $vm?.windowWidth < 300,
          'text-lg': $vm?.windowWidth >= 300 && $vm?.windowWidth < 400,
          'text-xl': $vm?.windowWidth >= 400
        }" :title="getUsernameFromEmail(vm.user.email) || 'Rana Marketplace'">
          {{ vm.user?.jwtToken ? getUsernameFromEmail(vm.user.email) : 'Rana Marketplace' }}
        </span>
        <span 
        class="truncate text-primary" :class="{
          'text-sm': $vm?.windowWidth < 300,
          'text-md': $vm?.windowWidth >= 300
        }">
          {{ vm.user?.jwtToken ? 'Connected' : 'Guest' }}
        </span>
      </div>
    </div>

    <!-- Right side - Controls -->
    <div 
    class="flex h-16 flex-shrink-0 flex-col justify-between" :class="{
      'max-w-[40%]': $vm?.windowWidth < 400,
      'max-w-[30%]': $vm?.windowWidth >= 400
    }">
      <!-- Status indicators - Not sure about what they detect - Missing imported green lights (/rana-app/src/components/icons/IconRanaGreenLight.vue) for successfull connection indicator -->
      <!--Leaving the div wiht commented out lights to reserve the space on UI (if we add or remove them the layout stays the same)-->
      <div class="flex items-center justify-end space-x-1">
        <!-- <div class="group">
          <IconRanaOfflineLight class="h-[1.5rem] w-[1.5rem] text-secondary-accent/80 " />
          Tooltip
          <div 
          class="absolute -right-12 top-8 hidden
                    group-hover:block
                    bg-[hsl(var(--accent))] text-neutral text-xs
                    rounded-md px-2 py-1 whitespace-nowrap
                    transform -translate-x-1/2 z-10">
            Nothing in Cart 
          </div>
        </div>
        <div class="group">
          <IconRanaOfflineLight class="h-[1.5rem] w-[1.5rem] text-secondary-accent/80 " />
          Tooltip
          <div 
          class="absolute -right-12 top-8 hidden
                    group-hover:block
                    bg-[hsl(var(--accent))] text-neutral text-xs
                    rounded-md px-2 py-1 whitespace-nowrap
                    transform -translate-x-1/2 z-10">
            Not Signed In
          </div>
        </div>
        <div class="group">
          <IconRanaOfflineLight class="h-[1.5rem] w-[1.5rem] text-secondary-accent/80 " />
          Tooltip
          <div 
          class="absolute -right-14 top-8 hidden
                    group-hover:block
                    bg-[hsl(var(--accent))] text-neutral text-xs
                    rounded-md px-2 py-1 whitespace-nowrap
                    transform -translate-x-1/2 z-10">
            No VPN Connection
          </div>
        </div> -->
      </div>
      <div class="inline-flex ">
        <!-- Sign In button or Three Dot Menu when logged in - Menu not functional / should have the feedback inside -->
        <template v-if="!vm.user?.jwtToken">
            <button 
            class="h-8 whitespace-nowrap px-4 rounded bg-[hsl(var(--secondary-accent))] text-sm text-black"
            @click="emit('open-login-panel')"
            @touchstart="[emit('open-login-panel'), openSigninDrawer()]"
            >
            Sign In
            </button>
        </template>

        <!-- Menu trigger and feedback -->
        <div class="flex w-full items-center justify-end" @click="IsthreeDotMenuOpen = false">
          <!-- Menu trigger -->
          <div v-if="vm.user?.jwtToken">
            <svg 
            id="radix-vue-dropdown-menu-trigger-v-19" width="40" height="40" viewBox="0 0 40 40" fill="none"
              xmlns="http://www.w3.org/2000/svg" type="button" aria-haspopup="menu" aria-expanded="false"
              data-state="closed" class="w-8 h-8 outline-none cursor-pointer" data-testid="cluster-info-three-dot-menu"
              @click="OpenThreeDotMenu">
              <rect x="1" y="1" width="38" height="38" rx="3" fill="#021B27" stroke="#02877F" stroke-width="2" />
              <circle cx="20.1087" cy="10.6087" r="2.6087" fill="#00CAAA" />
              <circle cx="20.1087" cy="29.3913" r="2.6087" fill="#00CAAA" />
              <circle cx="20.1087" cy="20" r="2.6087" fill="#00CAAA" />
            </svg>
            <div 
            v-if="vm.user?.jwtToken && IsThreeDotMenuOpen"
              class="absolute right-2 mt-2 w-48 rounded-md shadow-lg bg-[hsl(var(--neutral))] border border-[#02877F] z-20">
              <div class="py-1 rounded-md bg-[#021B27]">
                <ul class="divide-y divide-[#02877F]/30">
                  <li class="px-4 py-2 hover:bg-[#02877F]/20 cursor-pointer">
                    <a 
                    class="flex items-center justify-between text-[hsl(var(--primary))]"
                      href="https://login.rana.ai/profile" target="_blank" rel="noopener noreferrer" tabindex="0">
                      <span>Profile</span>
                      <svg width="14" height="18" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-0.5">
                        <path
                          d="M0 18.9525C0 17.0183 4.67642 14.1488 7.69766 13.9621C7.89471 13.9499 8.08723 14.011 8.26204 14.1027C8.79383 14.3819 10.1116 14.9525 12 14.9525C13.8884 14.9525 15.2062 14.3819 15.738 14.1027C15.9128 14.011 16.1053 13.9499 16.3023 13.9621C19.3236 14.1488 24 17.0183 24 18.9525C24 20.9525 24 19.9525 24 21.9525C24 23.9525 22 25.9525 22 25.9525C22 25.9525 20 27.9525 12 27.9525C4 27.9525 2 25.9525 2 25.9525C2 25.9525 0 23.9525 0 21.9525C0 19.9525 0 20.9525 0 18.9525Z"
                          fill="#00CAAA" />
                        <path
                          d="M18 6.95245C18 10.2662 15.3137 12.9525 12 12.9525C8.68629 12.9525 6 10.2662 6 6.95245C6 3.63875 8.68629 0.952454 12 0.952454C15.3137 0.952454 18 3.63875 18 6.95245Z"
                          fill="#00CAAA" />
                      </svg>

                    </a>
                  </li>
                  <li class="px-4 py-2 hover:bg-[#02877F]/20 cursor-pointer">
                    <div 
                    class="flex items-center justify-between text-[hsl(var(--primary))]" tabindex="0"
                      @click="$emit('leaveFeedback'); handleClickFeedback()">
                      <span :hide-border="true">Leave Feedback</span>
                      <IconLeaveFeedback class="w-6 h-6 -mr-1" aria-label="Leave Feedback" hide-border="true" />
                    </div>
                  </li>
                  <li
                    class="px-4 py-2 hover:bg-[#02877F]/20 cursor-pointer flex items-center justify-between text-[hsl(var(--primary))]"
                    @click="logout">
                    <span>Logout</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path 
                      d="M16 17L21 12M21 12L16 7M21 12H9" stroke="#00CAAA" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path 
                      d="M9 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H9" stroke="#00CAAA"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <!-- Feedback icon - can be moved to dropdown menu if needed -->
          <div 
          v-if="!vm.user?.jwtToken" class="relative cursor-pointer group ml-2" tabindex="0"
            @click="$emit('leaveFeedback')" @keyup.enter="$emit('leaveFeedback')">
            <IconLeaveFeedback class="w-8 h-8" aria-label="Leave Feedback" @click="handleClickFeedback" />
            <div 
            class="absolute left-1/4 top-full mt-2 hidden
                    group-hover:block
                    bg-[hsl(var(--accent))] text-neutral text-xs
                    rounded-md px-2 py-1 whitespace-nowrap
                    transform -translate-x-1/2 z-10">
              Contact Us
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>